import * as React from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import {Buttons} from "../../../components/buttons/Buttons";
import {Loader} from "../../../components/Utils/loader/Loader";
import {AppTranslations} from "../../../components/Utils/translations/AppTranslations";
import {AlarmStatus} from "../data/AlarmsMonitorData";
import {AlarmsMonitorDataMapper} from "../utils/AlarmsMonitorDataMapper";
import {icons} from "@vebox/etui-core";

const LIST_ELEMENTS_LIMIT = 100;
const LIST_WIDTH: number = 570;
declare var AppTranslations: AppTranslations;

interface EquipmentAlarmsCount {
    equipment: number;
    alarms: number;
}

interface AlarmInfo {
    alarmId: number;
    equipmentId: number;
    equipmentType: string;
    equipmentCode: string;
    equipmentManufacturerCode: string;
    equipmentAddress: string;
    alarmType: string;
    alarmTypeKey: string;
    receivedInfo: string;
    alarmStatus: number;
    falseAlarm: boolean;
    date: Date;
    severity: string;
}

enum ListStatus {
    Loading,
    Loaded,
}

interface Props {
    status: AlarmStatus;
    categoryId: number;
    severityGuid: string;
    isOpened: boolean;
    onListElementClick: (alarmId: number, equipmentId: number) => void;
    onCloseList: () => void;
    filters: string;
    useAsFilter: () => void;
}

interface State {
    total: EquipmentAlarmsCount;
    nextOffset: number;
    elements: AlarmInfo[];
    right: number;
    status: ListStatus;
    reload: boolean;
}

export class MonitorAlarmsList extends React.Component<Props, State> {

    private static getEquipmentName(element: AlarmInfo) {
        if (element.equipmentCode != null) {
            return element.equipmentCode;
        }
        return element.equipmentManufacturerCode;
    }

    constructor(props) {
        super(props);
        this.loadMore = this.loadMore.bind(this);
        this.load = this.load.bind(this);
        this.onAlarmClick = this.onAlarmClick.bind(this);
        this.updateDimensions = this.updateDimensions.bind(this);
        this.state = {elements: [], total: null, nextOffset: 0, right: null, status: ListStatus.Loading, reload: false};
        this.load(false);
        this.loadMore()
    }

    public componentWillUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>, nextContext: any): void {
        if (nextProps.categoryId != null
            && this.props.categoryId != null
            && nextProps.severityGuid !== this.props.severityGuid) {
            this.setState({reload: true});
        }
    }

    public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        if (this.state.reload && !prevState.reload) {
            this.load(true);
            this.loadMore();
        }
    }

    public componentDidMount(): void {
        window.addEventListener("resize", this.updateDimensions);
        this.updateDimensions();
    }

    public render() {
        if (!this.props.isOpened) {
            return null;
        }
        const style = this.state.right != null ? {right: this.state.right} : {};
        return (
            <div
                className={"monitor-alarms-list"}
                style={style}
                ref={"ALARMS_LIST"}
            >
                <div className={"monitor-alarms-list__list"}>
                    {this.renderList()}
                </div>
                <div className={"monitor-alarms-list__footer"}>
                    {this.state.total != null &&
                        <div className={"equipment-count"}>
                            {AppTranslations.alarmsMonitor.texts.equipmentInList}: <span>{this.state.total.equipment}</span>
                        </div>
                    }
                    <Buttons.ButtonWithIcon
                        key={2}
                        code={"close"}
                        label={AppTranslations.alarmsMonitor.texts.close}
                        svgName={icons.CLOSE}
                        selected={false}
                        onClick={this.props.onCloseList}
                        styles={{root: {mr: 1}}}
                    />
                    <Buttons.ButtonWithIcon
                        key={1}
                        code={"use_as_filter"}
                        variant={"primary"}
                        label={AppTranslations.alarmsMonitor.texts.useAsFilters}
                        icon={"images/buttons/icon-filter.png"}
                        id={"useAlarmsAsFilters"}
                        selected={false}
                        onClick={this.props.useAsFilter}
                    />
                </div>
            </div>
        );
    }

    private updateDimensions() {
        const content = (this.refs.ALARMS_LIST as HTMLDivElement);
        if (content == null) {
            return;
        }
        const dimensions: any = content.getBoundingClientRect();
        if (window.innerWidth - dimensions.x < LIST_WIDTH && this.state.right == null) {
            this.setState({right: 0});
        } else {
            this.setState({right: null});
        }
    }

    private renderList() {
        if (this.state.total == null) {
            return <Loader/>;
        }
        return (
            <React.Fragment>
                {this.state.status === ListStatus.Loading && <Loader/>}
                <InfiniteScroll
                    dataLength={this.state.elements.length}
                    next={this.loadMore}
                    hasMore={this.state.elements.length < this.state.total.alarms}
                    loader={null}
                    endMessage={null}
                    height={270}
                >
                    {this.renderElements()}
                </InfiniteScroll>
            </React.Fragment>
        );
    }

    private renderElements() {
        return this.state.elements.map(
            (element, index) => {
                const backgroundColor = {
                    backgroundColor: AlarmsMonitorDataMapper
                        .getSeverityColor(AppTranslations.alarmsMonitor.severities, element.severity),
                };
                return (
                    <div
                        key={index}
                        className={"monitor-alarm-element"}
                        data-equipment={element.equipmentId}
                        data-alarm={element.alarmId}
                        onClick={this.onAlarmClick}
                    >
                        <div className={"equipment-info"}>
                            <p className={"equipment-detail"}>
                                {element.equipmentType} {MonitorAlarmsList.getEquipmentName(element)}
                            </p>
                            <p className={"equipment-address"}>{element.equipmentAddress}</p>
                        </div>
                        <div className={"alarm-info"}>
                            <div className={"alarm-severity-circle"} style={backgroundColor}>
                                {element.falseAlarm && <p>F</p>}
                            </div>
                            <div className={"alarm-detail"}>
                                <p className={"alarm-type"}>{element.alarmType}</p>
                                <p className={"alarm-time"}>{element.receivedInfo}</p>
                            </div>
                        </div>
                    </div>
                );
            },
        );
    }

    private onAlarmClick(e: React.MouseEvent<HTMLDivElement, MouseEvent>): void {
        // tslint:disable-next-line:radix
        const alarmId: number = parseInt(e.currentTarget.dataset.alarm);
        // tslint:disable-next-line:radix
        const equipmentId: number = parseInt(e.currentTarget.dataset.equipment);
        this.props.onListElementClick(alarmId, equipmentId);
        this.props.onCloseList();
    }

    private load(setAsLoading: boolean) {
        if (setAsLoading) {
            this.setState({status: ListStatus.Loading, elements: [], total: null, nextOffset: 0, reload: false});
        }
        const severityGuid = this.props.severityGuid;
        const categoryId = this.props.categoryId;
        Ajax.ajaxRequestForJson(
            {
                url: "getAlarmsForEquipmentsGroupedForCategoryStateCount",
                data: this.completeFilters(),
                type: "POST",
                success: (count: EquipmentAlarmsCount) => {
                    if (this.props.categoryId !== categoryId || this.props.severityGuid !== severityGuid) {
                        return;
                    }
                    this.setState({total: count});
                },
                error: () => {
                    console.error("Error loading categories statistics");
                },
            },
        );
    }

    private loadMore() {
        this.setState({status: ListStatus.Loading});
        const severityGuid = this.props.severityGuid;
        const categoryId = this.props.categoryId;
        Ajax.ajaxRequestForJson(
            {
                url: "getAlarmsForEquipmentsGroupedForCategoryState",
                data: this.completeFilters(),
                type: "POST",
                success: (items: AlarmInfo[]) => {
                    if (this.props.categoryId !== categoryId || this.props.severityGuid !== severityGuid) {
                        return;
                    }
                    const elements: AlarmInfo[] = this.state.elements;
                    elements.push(...items);
                    this.setState(
                        {
                            elements,
                            nextOffset: this.state.nextOffset + LIST_ELEMENTS_LIMIT,
                            status: ListStatus.Loaded,
                        },
                    );
                },
                error: () => {
                    console.error("Error loading categories statistics");
                },
            },
        );
    }

    private completeFilters() {
        const filters = JSON.parse(this.props.filters);
        filters.limitlessListFiltersCategory.offset = this.state.nextOffset;
        filters.limitlessListFiltersCategory.limit = LIST_ELEMENTS_LIMIT;
        filters.alarmPanelFiltersCategory.categoryId = this.props.categoryId;
        filters.alarmPanelFiltersCategory.alarmStatus = AlarmsMonitorDataMapper.toAlarmStatusCode(this.props.status);
        filters.alarmPanelFiltersCategory.alarmSeverity = this.props.severityGuid;
        return JSON.stringify(filters);
    }
}
